import TenantName from 'components/TenantName';
import WaitingDots from 'components/WaitingDots';
import LedgerAccountSelect from 'components/LedgerAccountSelect';
import {
    UncategorizedLedgerAccount,
    getExpenseLedgerAccount,
    setExpenseLedgerAccount
} from 'common/LedgerAccounts';
import {Expense} from 'models/Expense';
import React, { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import {connect} from 'react-redux';
import {Link} from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import { bindActionCreators } from 'redux';
import { editExpense } from 'store/actions/Expenses';
import {RootState} from 'store/store';
import ExpenseEditButton from './ExpenseEditButton';
import { useOfficeTranslation } from 'i18n/OfficeTranslation';
import { AccountState } from 'store/reducers/Accounts';

interface Props {
    accountState: AccountState;
    expense: Expense;
    style?: CSSProperties;
}

interface Actions {
    editExpense: (expense: Expense) => void;
}

const formatCurrencyAmount = (amount: number) => {
    if (!Number.isFinite(amount)) {
        return "0.00";
    }

    return amount.toFixed(2);
};

const ExpenseRowIndex: React.FC<Props & Actions> = ({ accountState, expense, editExpense, style }) => {
    const { intlLocale, t } = useOfficeTranslation();
    const account = accountState.getAccountById(expense.account_id);
    const [ledgerAccount, setLedgerAccount] = useState(expense ? getExpenseLedgerAccount(expense) : UncategorizedLedgerAccount);
    const [isEditingTax, setIsEditingTax] = useState(false);
    const [taxValue, setTaxValue] = useState(expense ? formatCurrencyAmount(expense.tax) : "0.00");
    const desktopTaxInputRef = useRef<HTMLInputElement | null>(null);
    const mobileTaxInputRef = useRef<HTMLInputElement | null>(null);
    const skipTaxBlurSave = useRef(false);

    useEffect(() => {
        if (expense) {
            setLedgerAccount(getExpenseLedgerAccount(expense));
        }
    }, [expense]);

    useEffect(() => {
        if (expense && !isEditingTax) {
            setTaxValue(formatCurrencyAmount(expense.tax));
        }
    }, [expense, isEditingTax]);

    useEffect(() => {
        if (isEditingTax) {
            const input = typeof window.matchMedia === "function"
                && window.matchMedia("(max-width: 767.98px)").matches
                ? mobileTaxInputRef.current
                : desktopTaxInputRef.current;
            input?.focus();
            input?.select();
        }
    }, [isEditingTax]);

    if (expense === undefined) {
        return <WaitingDots />
    }

    const saveLedgerAccount = (value: string) => {
        const nextExpense = Object.assign(new Expense(), expense);
        setExpenseLedgerAccount(nextExpense, value);
        setLedgerAccount(value);
        editExpense(nextExpense);
    };

    const saveTax = () => {
        if (skipTaxBlurSave.current) {
            skipTaxBlurSave.current = false;
            return;
        }

        const parsedTax = parseFloat(taxValue.trim().replace(",", "."));
        setIsEditingTax(false);

        if (!Number.isFinite(parsedTax)) {
            setTaxValue(formatCurrencyAmount(expense.tax));
            return;
        }

        const nextTax = Math.round(parsedTax * 100) / 100;
        setTaxValue(formatCurrencyAmount(nextTax));

        if (nextTax === expense.tax) {
            return;
        }

        const nextExpense = Object.assign(new Expense(), expense);
        nextExpense.tax = nextTax;
        editExpense(nextExpense);
    };

    const cancelTaxEdit = () => {
        skipTaxBlurSave.current = true;
        setTaxValue(formatCurrencyAmount(expense.tax));
        setIsEditingTax(false);
        desktopTaxInputRef.current?.blur();
        mobileTaxInputRef.current?.blur();
    };

    const taxEditor = (mobile = false) => isEditingTax ? (
        <input
            ref={mobile ? mobileTaxInputRef : desktopTaxInputRef}
            type="number"
            step="0.01"
            aria-label={t("common.vat")}
            className="form-control form-control-sm"
            value={taxValue}
            onChange={event => setTaxValue(event.currentTarget.value)}
            onBlur={saveTax}
            onWheel={event => event.currentTarget.blur()}
            onKeyDown={event => {
                if (event.key === "Enter") {
                    event.preventDefault();
                    event.currentTarget.blur();
                }

                if (event.key === "Escape") {
                    event.preventDefault();
                    cancelTaxEdit();
                }
            }}
            style={{ width: mobile ? "100%" : 90 }}
        />
    ) : (
        <button
            type="button"
            onClick={() => setIsEditingTax(true)}
            className="p-0 text-nowrap"
            style={{
                background: "transparent",
                border: 0,
                borderBottom: "1px dotted currentColor",
                color: "inherit",
                cursor: "text",
            }}
        >
            &euro; {taxValue}
        </button>
    );

    return (
        <>
            <div className="d-none d-md-block border-bottom py-3" style={style}>
                <Row className="align-items-center g-3 h-100">
                <Col md={3}>
                    <Link className="d-block fw-semibold text-truncate" to={`/expenses/${expense.id}`}>
                        {expense.name}
                    </Link>
                    <div className="d-flex align-items-center gap-2 mt-1 small">
                        <span className="text-muted">
                            {new Intl.DateTimeFormat(intlLocale, { dateStyle: "medium" }).format(expense.GetDate())}
                        </span>
                        <Link className="text-truncate" to={`/accounts/${expense.account_id}`}>
                            {account?.legal_name ?? t("common.unknownSupplier")}
                        </Link>
                    </div>
                </Col>
                <Col md={2}>
                    <Link to={"/tenants/" + expense.tenant}>
                        <span className="badge bg-primary bg-soft text-primary font-size-12">
                            <TenantName fetch={false} id={expense.tenant} />
                        </span>
                    </Link>
                </Col>
                <Col md={3}>
                    <LedgerAccountSelect
                        compact={true}
                        value={ledgerAccount}
                        onChange={setLedgerAccount}
                        onSave={saveLedgerAccount}
                    />
                </Col>
                <Col md={1}>
                    <small className="d-block text-muted mb-1">{t("common.vat")}</small>
                    {taxEditor()}
                </Col>
                <Col md={2}>
                    <small className="d-block text-muted mb-1">{t("common.total")}</small>
                    <strong>{new Intl.NumberFormat(intlLocale, {style: "currency", currency: "EUR"}).format(expense.total)}</strong>
                    {expense.attachments > 0 &&
                        <span className="text-muted ms-2">
                            <i className="bx bx-file me-1" />{expense.attachments}
                        </span>
                    }
                </Col>
                <Col className="text-end" md={1}>
                    <ExpenseEditButton expense={expense} />
                </Col>
                </Row>
            </div>

            <div className="d-md-none border-bottom py-3" style={style}>
                <div className="d-flex justify-content-between align-items-start gap-3">
                    <div style={{ minWidth: 0 }}>
                        <Link className="d-block fw-semibold text-truncate" to={`/expenses/${expense.id}`}>
                            {expense.name}
                        </Link>
                        <div className="d-flex align-items-center gap-2 mt-1 small" style={{ minWidth: 0 }}>
                            <span className="text-muted flex-shrink-0">
                                {new Intl.DateTimeFormat(intlLocale, { dateStyle: "medium" }).format(expense.GetDate())}
                            </span>
                            <Link className="text-truncate" to={`/accounts/${expense.account_id}`}>
                                {account?.legal_name ?? t("common.unknownSupplier")}
                            </Link>
                        </div>
                    </div>
                    <div className="text-end flex-shrink-0">
                        <strong className="d-block">{new Intl.NumberFormat(intlLocale, {style: "currency", currency: "EUR"}).format(expense.total)}</strong>
                        {expense.attachments > 0 &&
                            <small className="text-muted">
                                <i className="bx bx-file me-1" />{expense.attachments}
                            </small>
                        }
                    </div>
                </div>

                <div className="d-flex justify-content-between align-items-center gap-2 mt-2">
                    <Link className="text-truncate" style={{ minWidth: 0 }} to={"/tenants/" + expense.tenant}>
                        <span className="badge bg-primary bg-soft text-primary font-size-12 text-truncate mw-100">
                            <TenantName fetch={false} id={expense.tenant} />
                        </span>
                    </Link>
                    <div className="flex-shrink-0" style={{ width: 38 }}>
                        <ExpenseEditButton expense={expense} />
                    </div>
                </div>

                <div className="d-flex align-items-end gap-2 mt-2">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <LedgerAccountSelect
                            compact={true}
                            value={ledgerAccount}
                            onChange={setLedgerAccount}
                            onSave={saveLedgerAccount}
                        />
                    </div>
                    <div className="flex-shrink-0" style={{ width: 90 }}>
                        <small className="d-block text-muted mb-1">{t("common.vat")}</small>
                        {taxEditor(true)}
                    </div>
                </div>
            </div>
        </>
    );
}

const mapStateToProps = (state: RootState) => {
    return {
        accountState: state.Accounts,
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return bindActionCreators({ editExpense }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(ExpenseRowIndex);
